import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';
import {
  FaBox,
  FaList,
  FaSignOutAlt,
  FaChartBar,
  FaUsers,
  FaCog,
  FaEye,
  FaPlus,
  FaEdit,
  FaTrash,
  FaArrowLeft,
  FaArrowRight,
  FaBell,
  FaSearch,
  FaWhatsapp,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope
} from 'react-icons/fa';
import { branches, updateBranch, addBranch, getUpdatedBranches } from '../../utils/branchesData';
import './BranchManagement.css';

function BranchManagement() {
  const { adminUser, logout } = useAdmin();
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBranch, setEditingBranch] = useState(null);
  const [isAddingBranch, setIsAddingBranch] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    whatsapp: '',
    email: '',
    address: '',
    isActive: true,
    hasDelivery: false,
    hasWhatsApp: false
  });

  useEffect(() => {
    loadBranches();
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const loadBranches = async () => {
    try {
      setLoading(true);
      
      // Get updated branches from branchesData.js
      const currentBranches = getUpdatedBranches();
      
      // Convert to admin format
      const adminBranches = currentBranches.map(branch => {
        const hasDelivery = branch.hasDelivery || false;
        const hasWhatsApp = branch.hasWhatsApp || false;
        const isFullyActive = hasDelivery && hasWhatsApp;
        
        return {
          id: branch.id,
          name: branch.name,
          whatsapp: branch.whatsapp,
          email: `${branch.city.toLowerCase()}@pharmacare.mw`,
          address: `${branch.location}, ${branch.address}, ${branch.city}`,
          isActive: isFullyActive,
          hasDelivery: hasDelivery,
          hasWhatsApp: hasWhatsApp
        };
      });
      
      console.log('Loaded branches:', adminBranches); // Debug log
      setBranches(adminBranches);
    } catch (error) {
      console.error('Error loading branches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBranch = () => {
    setIsAddingBranch(true);
    setEditingBranch(null);
    setFormData({
      name: '',
      whatsapp: '',
      email: '',
      address: '',
      isActive: false, // New branches start as inactive
      hasDelivery: false,
      hasWhatsApp: false
    });
  };

  const handleEdit = (branch) => {
    setEditingBranch(branch);
    setIsAddingBranch(false);
    setFormData({
      name: branch.name,
      whatsapp: branch.whatsapp,
      email: branch.email,
      address: branch.address,
      isActive: branch.isActive,
      hasDelivery: branch.hasDelivery || false,
      hasWhatsApp: branch.hasWhatsApp || false
    });
  };

  const handleSave = () => {
    if (isAddingBranch) {
      // Determine if branch is fully active based on features
      const isFullyActive = formData.hasDelivery && formData.hasWhatsApp;
      
      // Add new branch
      const newBranch = {
        id: Math.max(...branches.map(b => b.id)) + 1, // Generate new ID
        ...formData,
        isActive: isFullyActive
      };
      
      const updatedBranches = [...branches, newBranch];
      setBranches(updatedBranches);
      
      // Add to original branches data
      const newOriginalBranch = {
        id: newBranch.id,
        name: formData.name,
        location: formData.address.split(',')[0] || '',
        address: formData.address,
        city: formData.address.split(',').pop()?.trim() || 'Unknown',
        phone: [formData.whatsapp],
        whatsapp: formData.whatsapp,
        enabled: isFullyActive,
        hasDelivery: formData.hasDelivery,
        hasWhatsApp: formData.hasWhatsApp
      };
      
      // Use the addBranch utility function
      addBranch(newOriginalBranch);
      
      // Notify main site of branch updates
      window.dispatchEvent(new CustomEvent('branchesUpdated'));
      
      setIsAddingBranch(false);
      setFormData({ name: '', whatsapp: '', email: '', address: '', isActive: true, hasDelivery: false, hasWhatsApp: false });
    } else if (editingBranch) {
      // Determine if branch is fully active based on features
      const isFullyActive = formData.hasDelivery && formData.hasWhatsApp;
      
      // Update existing branch
      const updatedBranches = branches.map(branch =>
        branch.id === editingBranch.id
          ? { ...branch, ...formData, isActive: isFullyActive }
          : branch
      );
      setBranches(updatedBranches);
      
      // Update the original branches data using the utility function
      updateBranch(editingBranch.id, {
        name: formData.name,
        whatsapp: formData.whatsapp,
        enabled: isFullyActive,
        hasDelivery: formData.hasDelivery,
        hasWhatsApp: formData.hasWhatsApp
      });
      
      // Notify main site of branch updates
      window.dispatchEvent(new CustomEvent('branchesUpdated'));
      
      setEditingBranch(null);
      setFormData({ name: '', whatsapp: '', email: '', address: '', isActive: true, hasDelivery: false, hasWhatsApp: false });
    }
  };

  const handleCancel = () => {
    setEditingBranch(null);
    setIsAddingBranch(false);
    setFormData({ name: '', whatsapp: '', email: '', address: '', isActive: true, hasDelivery: false, hasWhatsApp: false });
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="admin-dashboard">
      {/* Modern Sidebar */}
      <div className="admin-sidebar">
        <div className="sidebar-header">
          <div className="logo-container">
            <img src="/pharmacare-logo.png" alt="Pharmacare" className="admin-logo" />
            <div className="logo-text">
              <h2>Pharmacare</h2>
              <span>Admin Panel</span>
            </div>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">
            <h3>Overview</h3>
            <Link to="/admin" className="nav-item">
              <FaChartBar />
              <span>Dashboard</span>
              <FaArrowRight />
            </Link>
          </div>

          <div className="nav-section">
            <h3>Management</h3>
            <Link to="/admin/products" className="nav-item">
              <FaBox />
              <span>Products</span>
              <FaArrowRight />
            </Link>
            <Link to="/admin/categories" className="nav-item">
              <FaList />
              <span>Categories</span>
              <FaArrowRight />
            </Link>
            <div className="nav-item active">
              <FaMapMarkerAlt />
              <span>Branches</span>
              <FaArrowRight />
            </div>
          </div>

          <div className="nav-section">
            <h3>System</h3>
            <div className="nav-item">
              <FaUsers />
              <span>Users</span>
              <span className="badge">Soon</span>
            </div>
            <div className="nav-item">
              <FaCog />
              <span>Settings</span>
              <span className="badge">Soon</span>
            </div>
          </div>
        </nav>
      </div>

      {/* Main Content */}
      <div className="admin-main">
        {/* Top Header */}
        <div className="admin-topbar">
          <div className="topbar-left">
            <h1>Branch Management</h1>
            <p>Manage branch WhatsApp numbers and contact information</p>
          </div>
          <div className="topbar-right">
            <button className="add-branch-btn" onClick={handleAddBranch}>
              <FaPlus /> Add Branch
            </button>
            <div className="user-menu">
              <div className="user-avatar">
                {adminUser?.username?.charAt(0).toUpperCase()}
              </div>
              <div className="user-info">
                <span>{adminUser?.username}</span>
                <small>Administrator</small>
              </div>
              <button onClick={handleLogout} className="logout-btn">
                <FaSignOutAlt />
              </button>
            </div>
          </div>
        </div>

        {/* Dashboard Content */}
        <div className="dashboard-content">
          <div className="page-header">
            <button className="back-button" onClick={() => navigate('/admin')}>
              <FaArrowLeft /> Back to Dashboard
            </button>
          </div>

          <div className="content-container">
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading branches...</p>
              </div>
            ) : (
              <div className="branches-grid">
                {branches.map((branch) => (
                  <div key={branch.id} className={`branch-card ${branch.isActive ? 'active-branch' : 'inactive-branch'}`}>
                    <div className="branch-header">
                      <div className="branch-status">
                        <span className={`status-badge ${branch.isActive ? 'active' : 'inactive'}`}>
                          {branch.isActive ? 'Fully Active' : 'Operational Only'}
                        </span>
                      </div>
                      <h3>{branch.name}</h3>
                    </div>
                    
                    <div className="branch-features">
                      <div className="feature-item">
                        <span className={`feature-icon ${branch.hasDelivery ? 'available' : 'unavailable'}`}>
                          {branch.hasDelivery ? '✅' : '🚫'}
                        </span>
                        <span className={`feature-text ${branch.hasDelivery ? 'available' : 'unavailable'}`}>
                          {branch.hasDelivery ? 'Delivery Service' : 'No Delivery Service'}
                        </span>
                      </div>
                      <div className="feature-item">
                        <span className={`feature-icon ${branch.hasWhatsApp ? 'available' : 'unavailable'}`}>
                          {branch.hasWhatsApp ? '✅' : '🚫'}
                        </span>
                        <span className={`feature-text ${branch.hasWhatsApp ? 'available' : 'unavailable'}`}>
                          {branch.hasWhatsApp ? 'WhatsApp Inquiries' : 'No WhatsApp Inquiries'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="branch-details">
                      <div className="branch-detail">
                        <FaWhatsapp className="detail-icon whatsapp" />
                        <div className="detail-content">
                          <span className="detail-label">WhatsApp</span>
                          <span className="detail-value">{branch.whatsapp}</span>
                        </div>
                      </div>
                      <div className="branch-detail">
                        <FaEnvelope className="detail-icon email" />
                        <div className="detail-content">
                          <span className="detail-label">Email</span>
                          <span className="detail-value">{branch.email}</span>
                        </div>
                      </div>
                      <div className="branch-detail">
                        <FaMapMarkerAlt className="detail-icon location" />
                        <div className="detail-content">
                          <span className="detail-label">Address</span>
                          <span className="detail-value">{branch.address}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="branch-actions">
                      <button 
                        className="edit-btn"
                        onClick={() => handleEdit(branch)}
                      >
                        <FaEdit /> Edit Branch
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Branch Modal */}
      {(editingBranch || isAddingBranch) && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>{isAddingBranch ? 'Add New Branch' : 'Edit Branch'}</h2>
            <div className="form-group">
              <label>Branch Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter branch name"
              />
            </div>
            <div className="form-group">
              <label>WhatsApp Number</label>
              <input
                type="text"
                value={formData.whatsapp}
                onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                placeholder="Enter WhatsApp number"
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email address"
              />
            </div>
            <div className="form-group">
              <label>Address</label>
              <textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Enter branch address"
                rows="3"
              />
            </div>
            <div className="form-group">
              <div className="info-note">
                <small>Branch status will be automatically determined based on delivery and WhatsApp features.</small>
              </div>
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.hasDelivery}
                  onChange={(e) => setFormData({ ...formData, hasDelivery: e.target.checked })}
                />
                Has Delivery Service
              </label>
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.hasWhatsApp}
                  onChange={(e) => setFormData({ ...formData, hasWhatsApp: e.target.checked })}
                />
                Has WhatsApp Inquiries
              </label>
            </div>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={handleCancel}>
                Cancel
              </button>
              <button className="submit-btn" onClick={handleSave}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BranchManagement;
